<template>
<div>
  <CCard v-if="item">
    <CCardHeader>
      <CIcon name="cil-restaurant"/>
      <h5 class="d-inline ml-2">
        {{ item?.restaurant_name }}
      </h5>

      <div class="card-header-actions d-flex flex-gap-1">
        <div class="d-flex align-items-center border-right">
          <CButton variant="ghost" shape="pill" target="_blank" v-if="item.restaurant_slug"
            :href="item.restaurant_slug_path | addUriScheme" v-c-tooltip="{ content: item.restaurant_slug_path }">
            <CIcon name="cil-restaurant" />
          </CButton>

          <CButton variant="ghost" shape="pill" target="_blank" v-if="item.website_etenonline_be"
            :href="item.website_etenonline_be | addUriScheme" v-c-tooltip="{ content: item.website_etenonline_be }">
            <CIcon name="cil-link" />
          </CButton>

          <CButton class="mr-1" variant="ghost" shape="pill" target="_blank" v-if="item.website"
            :href="item.website | addUriScheme" v-c-tooltip="{ content: item.website }">
            <CIcon name="cil-external-link" />
          </CButton>
        </div>

        <div class="d-flex align-items-center flex-gap-1">
          <CButton color="danger" variant="ghost" shape="pill"
            v-c-tooltip="{ content: 'Restaurant History' }"
            @click="showLatestActivities()"
          >
            <CIcon name="cil-fingerprint" size="lg" />
          </CButton>

          <CButton color="primary" variant="ghost" shape="pill"
            v-c-tooltip="{ content: 'Restaurant Messages' }"
            @click="showMessages()"
          >
            <CIcon name="cil-envelope-closed" size="lg" />
          </CButton>
        </div>

        <div class="d-flex align-items-center flex-gap-1 border-left">
          <CButton class="ml-1" color="info" variant="ghost" shape="pill"
            v-c-tooltip="{ content: 'Edit Restaurant' }"
            :to="{ name: 'Edit Restaurant', params: { id: itemId }}"
          >
            <CIcon name="cil-align-left" size="lg" />
          </CButton>

          <CButton color="dark" :to="prevRoute">
            <CIcon name="cil-arrow-thick-left" class="align-bottom" /> Back
          </CButton>
        </div>
      </div>
    </CCardHeader>

    <CCardBody class="pb-0">

      <div class="d-flex flex-column flex-md-row flex-gap-2 mb-4">
        <div class="flex-grow-1">
          <UnsignedContract
            v-if="item.email && !item.upload_contract"
            :id="item.id"
            :email="item.email"
          />
        </div>
        <div>
          <CAlert v-if="item.hasdeliveryService"  color="success" class="d-flex flex-gap-2 align-items-center mb-0 h-100">
            <CIcon name="cil-truck" size="2xl" />
            <div>
              <strong>Delivery Service enabled.</strong>
              <div v-if="!item.hubs_count" class="d-flex flex-gap-2 align-items-center text-danger font-weight-bold flash-it">
                ⚠ Add the postcode ({{ item.restaurant_postcode }}) to the scope of a hub.
              </div>
            </div>
          </CAlert>
        </div>
      </div>

      <div class="internal-note-panel mb-4">
        <div class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-2 flex-gap-2">
          <label class="font-weight-bold mb-0">
            <CIcon name="cil-notes" class="mr-1" />
            Internal Note
          </label>
          <div class="d-flex flex-wrap flex-gap-2">
            <CButton
              color="success"
              size="sm"
              :disabled="savingInternalNote"
              @click="saveInternalNote"
            >
              <CSpinner v-if="savingInternalNote" size="sm" class="mr-1" />
              <CIcon v-else name="cil-save" class="mr-1" />
              Save Internal Note
            </CButton>
            <CButton
              color="info"
              size="sm"
              :disabled="creatingInternalNoteTask"
              @click="createInternalNoteTask"
            >
              <CSpinner v-if="creatingInternalNoteTask" size="sm" class="mr-1" />
              <CIcon v-else name="cil-bolt" class="mr-1" />
              Create Follow-up Task
            </CButton>
          </div>
        </div>
        <CSelect
          class="mb-2"
          label="Assign follow-up to"
          :options="adminUserOptions"
          :value.sync="internalNoteTaskAssigneeId"
          :disabled="loadingAdminUsers"
        />
        <CTextarea
          class="mb-0"
          rows="3"
          placeholder="No internal note"
          :lazy="false"
          :value.sync="item.note_internal"
        />
      </div>

      <!-- Distance Threshold Info Alert -->
      <CAlert 
        v-if="item.delivery_service_distance_threshold_meter > 0"
        color="info" 
        class="mb-4"
        :show="true"
      >
        <CIcon name="cil-info" class="mr-2" />
        <strong>Distance Threshold Configuration:</strong>
        <div class="mt-2">
          This restaurant uses Menute couriers for orders within <strong>{{ item.delivery_service_distance_threshold_meter }}m</strong>. 
          Orders exceeding this distance will be delivered by the restaurant's own couriers instead of Menute delivery service.
        </div>
      </CAlert>

      <alert-section
        :successAlertMessage="successAlertMessage"
        :dismissSecs="dismissSecs"
        :dismissSuccessAlert="dismissSuccessAlert"
        :errorAlertMessage="errorAlertMessage"
        :showErrorAlert="showErrorAlert"
      />

      <CAlert
        v-if="hasRelatedRestaurants"
        color="warning"
        class="mb-4"
        :show="true"
      >
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center flex-gap-2">
          <div>
            <strong>
              <CIcon name="cil-link" class="mr-1" />
              Linked Restaurants
            </strong>
            <div class="small">
              This restaurant has a previous and/or next restaurant relation.
            </div>
          </div>
          <div class="d-flex flex-wrap flex-gap-1">
            <CButton
              v-if="previousRestaurantId"
              color="dark"
              size="sm"
              :to="{ name: 'View Restaurant', params: { id: previousRestaurantId } }"
            >
              <CIcon name="cil-arrow-thick-left" class="mr-1" />
              Previous: {{ previousRestaurantLabel }}
            </CButton>
            <CButton
              v-if="nextRestaurantId"
              color="dark"
              size="sm"
              :to="{ name: 'View Restaurant', params: { id: nextRestaurantId } }"
            >
              Next: {{ nextRestaurantLabel }}
              <CIcon name="cil-arrow-thick-right" class="ml-1" />
            </CButton>
          </div>
        </div>
      </CAlert>

      <div
        class="resto-header d-flex flex-column flex-sm-row align-items-center justify-content-between mb-4 p-3 h-100 flex-gap-3"
        :class="{'table-danger': item.none_active, 'table-success': !item.none_active}"
        :style="{ backgroundImage: 'url(' + item.hero_image?.image_url + ')' }"
      >
        <div class="logo">
          <CImg
            :src="getValidImageUrl(item.restaurant_logo_path)"
            class="logo__image"
            :class="{'resto-none-active': item.none_active, 'resto-active': !item.none_active}"
            placeholderColor="lightgray"
          />
          <div class="logo__content" @click="changelogo" role="button">
            <span class="logo__icon"><CIcon name="cil-camera" size="xl"/></span>
            <span class="logo__text">Change Logo</span>
          </div>
        </div>

        <div class="p-2 address-container">
          <address class="blockquote">
            <strong>{{ item.restaurant_name }}</strong><br>
            {{ item.restaurant_address }} <br>
            {{ item.restaurant_city }}, {{ item.countryId }} {{ item.restaurant_postcode }}<br>
            <abbr title="Phone Number"><CIcon name="cil-phone" />: {{ item.restaurant_tel }}</abbr><br>
            <abbr title="Mobile Number"><CIcon name="cil-mobile" />: {{ item.restaurant_gsm }}</abbr>
          </address>
          <address class="mb-0 blockquote">
            <abbr title="Email"><CIcon name="cil-at" />:
            <a :href="`mailto:${item.email}`">{{ item.email }}</a></abbr>
          </address>
          <div class="button ml-n2">
            <CLink :to="{ name: 'Edit Restaurant', params: { id: item.id }, query: { tab: 0 } }">
              <CIcon name="cil-pen" /> Edit Contact Info
            </CLink>
          </div>
        </div>

        <div class="action" @click="selectHeroImage">Change Hero Image</div>
      </div>

      <h5 class="mb-0 mt-5">Restaurant Settings</h5>
      <p>These settings help you quickly change important configs.</p>
      <div class="list-group mb-5 shadow">

        <div class="list-group-item">
          <div class="row align-items-center">
            <div class="col">
              <strong class="mb-2">
                Start Date
                <CBadge v-if="isSuperAdmin" shape="pill" color="warning">SuperAdmin</CBadge>
              </strong>
              <p class="text-muted mb-0">
                Published since: <strong>{{ formattedStartdate }}</strong>
              </p>
            </div>
            <div v-if="isSuperAdmin" class="col-12 col-lg-4 mt-3 mt-lg-0">
              <div class="d-flex flex-column flex-sm-row align-items-stretch flex-gap-1">
                <CInput
                  type="date"
                  class="mb-0 flex-grow-1"
                  :disabled="startdateForm.saving"
                  :value.sync="startdateForm.startdate"
                />
                <CButton
                  color="warning"
                  :disabled="!canSaveStartdate || startdateForm.saving"
                  @click="confirmStartdateChange"
                >
                  <CSpinner v-if="startdateForm.saving" size="sm" />
                  <CIcon v-else name="cil-save" />
                  Save
                </CButton>
              </div>
            </div>
          </div>
        </div>

        <div v-if="canManageSubsites" class="list-group-item">
          <div class="row align-items-start">
            <div class="col">
              <strong class="mb-2">
                Subsites / Domains
                <CBadge shape="pill" color="info">Admin</CBadge>
              </strong>
              <p class="text-muted mb-0">
                Manage the primary Menute domain and extra restaurant subsites used by DNS/export automation.
              </p>
            </div>
            <div class="col-12 col-lg-6 mt-3 mt-lg-0">
              <CInput
                class="mb-2"
                label="Primary domain"
                placeholder="restaurant-domain.be"
                :disabled="domainForm.saving"
                :value.sync="domainForm.website_etenonline_be"
              >
                <template #append>
                  <CLink
                    class="btn btn-info"
                    role="button"
                    target="_blank"
                    :href="domainForm.website_etenonline_be | addUriScheme"
                    :class="{ disabled: !domainForm.website_etenonline_be }"
                  >
                    <CIcon name="cil-external-link"/>
                  </CLink>
                </template>
              </CInput>

              <div
                v-for="(subsite, index) in domainForm.subsites"
                :key="`subsite-${index}`"
                class="d-flex flex-column flex-sm-row align-items-stretch flex-gap-1 mb-2"
              >
                <CInput
                  class="mb-0 flex-grow-1"
                  :label="index === 0 ? 'Additional subsites' : null"
                  placeholder="extra-domain.be"
                  :disabled="domainForm.saving"
                  :value.sync="subsite.domain"
                />
                <div class="d-flex align-items-end flex-gap-1">
                  <CButton
                    color="info"
                    :disabled="!subsite.domain"
                    :href="subsite.domain | addUriScheme"
                    target="_blank"
                  >
                    <CIcon name="cil-external-link" />
                  </CButton>
                  <CButton
                    color="danger"
                    :disabled="domainForm.saving"
                    @click="removeSubsiteDomain(index)"
                  >
                    <CIcon name="cil-trash" />
                  </CButton>
                </div>
              </div>

              <div class="d-flex flex-wrap flex-gap-1">
                <CButton
                  color="secondary"
                  variant="outline"
                  :disabled="domainForm.saving"
                  @click="addSubsiteDomain"
                >
                  <CIcon name="cil-plus" class="mr-1" />
                  Add subsite
                </CButton>
                <CButton
                  color="info"
                  :disabled="!canSaveSubsites || domainForm.saving"
                  @click="saveSubsites"
                >
                  <CSpinner v-if="domainForm.saving" size="sm" class="mr-1" />
                  <CIcon v-else name="cil-save" class="mr-1" />
                  Save domains
                </CButton>
              </div>
            </div>
          </div>
        </div>

        <div class="list-group-item">
          <div class="row align-items-center">
            <div class="col">
              <strong class="mb-2">Not Published</strong>
              <p class="text-muted mb-0">This setting controls whether the restaurant is actively published.</p>
            </div>
            <div class="col-auto">
              <CSwitch
                :checked.sync="item.none_active"
                @update:checked="updateRestaurantStatus"
                labelOn="YES"
                labelOff="NO"
                color="danger"
                shape="pill"
              />
            </div>
          </div>
        </div>

        <div class="list-group-item">
          <div class="row align-items-center">
            <div class="col">
              <strong class="mb-2">Today Closed</strong>
              <p class="text-muted mb-0">Quickly change if the place is open or closed.</p>
            </div>
            <div class="col-auto">
              <CSwitch
                :checked.sync="item.rest_closed_today"
                @update:checked="updateSettings({ 'rest_closed_today': item.rest_closed_today })"
                labelOn="YES"
                labelOff="NO"
                color="success"
                shape="pill"
              />
            </div>
          </div>
        </div>

        <div class="list-group-item">
          <div class="row align-items-start">
            <div class="col">
              <strong class="mb-2">Custom Opening Times</strong>
              <p class="text-muted mb-0">Easily set customized opening hours for the restaurant.</p>

              <div class="opening-times-summary mt-3">
                <div class="small text-muted mb-2">
                  Today: <strong>{{ todayScheduleLabel }}</strong>
                </div>

                <div class="d-flex flex-wrap flex-gap-2">
                  <div class="opening-times-summary__item">
                    <span class="text-muted">{{ todayOpeningField }}</span>
                    <code>{{ todayRawOpeningTimes }}</code>
                  </div>
                  <div class="opening-times-summary__item">
                    <span class="text-muted">{{ todayDeliveryField }}</span>
                    <code>{{ todayRawDeliveryTimes }}</code>
                  </div>
                </div>

                <div class="mt-3">
                  <div v-if="loadingTodayCustomOpeningTimes" class="small text-muted">
                    <CSpinner size="sm" class="mr-1" /> Loading today's custom opening time
                  </div>
                  <CAlert v-else-if="todayCustomOpeningTimesError" color="danger" class="mb-0 py-2">
                    {{ todayCustomOpeningTimesError }}
                  </CAlert>
                  <div v-else-if="todayCustomOpeningTimes.length" class="today-custom-times">
                    <div
                      v-for="(customTime, index) in todayCustomOpeningTimes"
                      :key="customTime.id || `${customTime.custom_date}-${index}`"
                      class="today-custom-times__row"
                    >
                      <CBadge color="warning" class="mr-2">Custom today</CBadge>
                      <span class="mr-3">
                        Pickup: <code>{{ rawTimeValue(customTime.pickup_times) }}</code>
                      </span>
                      <span>
                        Delivery: <code>{{ rawTimeValue(customTime.delivery_times) }}</code>
                      </span>
                    </div>
                  </div>
                  <div v-else class="small text-muted">
                    No custom opening time for today.
                  </div>
                </div>
              </div>
            </div>
            <div class="col-auto">
              <CButton
                :to="{ name: 'RestaurantOpeningTimes', params: { restaurant_id: item.id } }"
                size="sm"
                color="info"
                variant="ghost"
                shape="pill"
              >
                <CIcon name="cil-align-left" />
              </CButton>
            </div>
          </div>
        </div>

        <div v-if="canManageRepresentativeAccess" class="list-group-item">
          <div class="row align-items-start">
            <div class="col">
              <strong class="mb-2">
                Representative Access
                <CBadge shape="pill" color="info">Admin</CBadge>
              </strong>
              <div v-if="loadingRepresentatives" class="text-muted mt-2">
                <CSpinner size="sm" class="mr-1" /> Loading representatives
              </div>
              <div v-else-if="assignedRepresentatives.length" class="d-flex flex-wrap flex-gap-1 mt-2">
                <CBadge
                  v-for="representative in assignedRepresentatives"
                  :key="representative.id"
                  color="info"
                  class="representative-badge"
                >
                  {{ representativeDisplayName(representative) }}
                  <CButton
                    color="link"
                    size="sm"
                    class="representative-remove"
                    :disabled="savingRepresentative"
                    @click.stop="removeRepresentative(representative)"
                  >
                    <CIcon name="cil-x" />
                  </CButton>
                </CBadge>
              </div>
              <div v-else class="small text-muted mt-2">No representatives assigned.</div>
            </div>
            <div class="col-12 col-lg-5 mt-3 mt-lg-0">
              <div class="d-flex flex-column flex-sm-row align-items-stretch flex-gap-1">
                <CSelect
                  class="mb-0 flex-grow-1"
                  :disabled="loadingRepresentatives || savingRepresentative"
                  :options="representativeSelectOptions"
                  :value.sync="selectedRepresentative"
                />
                <CButton
                  color="info"
                  :disabled="!selectedRepresentative || savingRepresentative"
                  @click="assignRepresentative"
                >
                  <CSpinner v-if="savingRepresentative" size="sm" />
                  <CIcon v-else name="cil-user-follow" />
                  Assign
                </CButton>
              </div>
            </div>
          </div>
        </div>
        <div v-if="hasGeoMetadata" class="list-group-item">
          <div class="row align-items-center">
            <div class="col">
              <strong class="mb-2">
                Geo & SEO
                <CBadge shape="pill" :color="langBadgeColor(derivedLang)">
                  {{ (derivedLang || 'n/a').toUpperCase() }}
                </CBadge>
                <CBadge
                  v-if="storedLang"
                  shape="pill"
                  class="ml-1"
                  :color="hasLanguageMismatch ? 'warning' : langBadgeColor(storedLang)"
                >
                  Stored: {{ storedLang.toUpperCase() }}
                </CBadge>
              </strong>
              <p class="text-muted mb-0">
                Province: <strong>{{ geoProvinceName }}</strong>
                <span class="mx-1">|</span>
                Region: <strong>{{ geoRegionName }}</strong>
                <span class="mx-1">|</span>
                Derived language: <strong>{{ (derivedLang || 'n/a').toUpperCase() }}</strong>
              </p>
              <p v-if="isSuperAdmin" class="text-muted mb-0">
                Province code: <strong>{{ item.province?.code || 'n/a' }}</strong>
                <span class="mx-1">|</span>
                Region code: <strong>{{ item.region?.code || 'n/a' }}</strong>
              </p>
              <p v-if="hasLanguageMismatch" class="text-warning mb-0 mt-1">
                Stored restaurant language differs from postcode-derived language. SEO and Ads should be reviewed.
              </p>
            </div>
            <div class="col-auto text-right">
              <div class="small text-muted">Zip source</div>
              <strong>{{ item.restaurant_postcode || 'n/a' }}</strong>
              <div class="small text-muted">{{ item.restaurant_city || 'No city set' }}</div>
            </div>
          </div>
        </div>

        <div v-if="isSuperAdmin" class="list-group-item">
          <div class="row align-items-center">
            <div class="col">
              <strong class="mb-2">
                GPWEB
                <CBadge shape="pill" :color="item.gpweb ? 'success' : 'secondary'">
                  {{ item.gpweb ? 'G1' : 'G0' }}
                </CBadge>
              </strong>
              <p class="text-muted mb-0">
                Manually override gpweb status for this restaurant.
                <span v-if="item.gpweb_updated_at">Last update: {{ item.gpweb_updated_at }}</span>
                <span v-else>Last update: not set</span>
              </p>
            </div>
            <div class="col-auto">
              <CSwitch
                :checked.sync="item.gpweb"
                @update:checked="updateGpweb"
                labelOn="G1"
                labelOff="G0"
                color="success"
                shape="pill"
              />
            </div>
          </div>
        </div>

        <div v-if="isSuperAdmin" class="list-group-item">
          <div class="row align-items-center">
            <div class="col">
              <strong class="mb-2">
                Restaurant Slug
                <CBadge shape="pill" color="warning">SuperAdmin</CBadge>
              </strong>
              <p class="text-muted mb-0">
                Current slug: <strong>{{ item.restaurant_slug || 'not set' }}</strong>
                <span v-if="item.restaurant_slug_path" class="d-block small">{{ item.restaurant_slug_path }}</span>
              </p>
              <p class="text-warning mb-0 mt-1">
                Changing this does not create a redirect for the old public menu URL.
              </p>
            </div>
            <div class="col-12 col-lg-5 mt-3 mt-lg-0">
              <div class="d-flex flex-column flex-sm-row align-items-stretch flex-gap-1">
                <CInput
                  class="mb-0 flex-grow-1"
                  placeholder="restaurant-slug"
                  :disabled="slugForm.saving"
                  :value.sync="slugForm.restaurant_slug"
                />
                <CButton
                  color="warning"
                  :disabled="!canSaveSlug || slugForm.saving"
                  @click="confirmSlugChange"
                >
                  <CSpinner v-if="slugForm.saving" size="sm" />
                  <CIcon v-else name="cil-save" />
                  Save
                </CButton>
              </div>
            </div>
          </div>
        </div>

        <div class="list-group-item">
          <div class="row align-items-center">
            <div class="col">
              <strong class="mb-2" :class="{ 'text-success': item.hasdeliveryService }">
                <CIcon name="cil-truck" /> Delivery Service
              </strong>
              <p class="text-muted mb-0">Update Menute Delivery Service status.</p>
            </div>
            <div class="col-auto">
              <CButton
                :to="{ name: 'DeliveryServiceRestaurants' }"
                size="sm"
                color="info"
                variant="ghost"
                shape="pill"
              >
                <CIcon name="cil-align-left" />
              </CButton>
            </div>
          </div>
        </div>

        <div class="list-group-item">
          <div class="row align-items-center">
            <div class="col">
              <strong class="mb-2 mr-2">Send Restaurant Login Details to Holders</strong>
              <CBadge shape="pill" color="info">NEW</CBadge>
              <p class="text-muted mb-0">Send the restaurant's username and password directly to the restaurant emails.</p>
            </div>
            <div class="col-auto">
              <CButton size="sm" color="info" variant="ghost" shape="pill" @click="sendRestaurantDetails('login-details')">
                <CIcon name="cil-send" />
              </CButton>
            </div>
          </div>
        </div>

        <div class="list-group-item">
          <div class="row align-items-center">
            <div class="col">
              <strong class="mb-2 mr-2">Send Magicsoftware Link to Holders</strong>
              <CBadge shape="pill" color="info">NEW</CBadge>
              <p class="text-muted mb-0">Send Magicsoftware Link directly to the restaurant emails.</p>
            </div>
            <div class="col-auto">
              <CButton size="sm" color="info" variant="ghost" shape="pill" @click="sendRestaurantDetails('magicsoftware-link')">
                <CIcon name="cil-send" />
              </CButton>
            </div>
          </div>
        </div>
      </div>

      <CrmReports
        title="Restaurant CRM Reports"
        :restaurant-id="item.id"
      />

      <CRow>
        <!-- Account Balance -->
        <CCol class="widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Account Balance"
            :text= String(this.item.account_balance)
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-euro" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <CLink
                  :to="{ name: 'RestaurantPaymentList', params: { restaurant_id: item.id } }"
                  class="font-sm text-muted d-flex justify-content-between"
                  target="_self"
                >
                  Show Payments
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>


        <!-- Orders -->
        <CCol class="widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Orders"
            :text= String(this.item.restaurant_orders_count)
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-cart" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <CLink
                  :to="{ name: 'RestaurantOrderList', params: { restaurant_id: item.id } }"
                  class="font-sm text-muted d-flex justify-content-between"
                  target="_self"
                >
                  Show Orders
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>


        <!-- Average Delivery Distance -->
        <CCol class="widget delivery-distance-widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Delivery Distance"
            :text="averageDeliveryDistanceWidgetText"
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-speedometer" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <div class="delivery-distance-widget__meta px-1 pb-1">
                  {{ maximumDeliveryDistanceWidgetText }}
                </div>
                <div class="delivery-distance-widget__meta delivery-distance-widget__orders px-1 pb-1">
                  {{ averageDeliveryDistanceOrdersText }}
                </div>
                <CLink
                  :to="{ name: 'RestaurantOrderList', params: { restaurant_id: item.id } }"
                  class="font-sm text-muted d-flex justify-content-between"
                  target="_self"
                >
                  Show Orders
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>


        <!-- Invoices -->
        <CCol class="widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Invoices"
            :text= String(this.item.invoices_count)
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-file" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <CLink
                  :to="{ name: 'RestaurantInvoices', params: { restaurant_id: item.id } }"
                  class="font-sm text-muted d-flex justify-content-between"
                  target="_self"
                >
                  Show Invoices
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>


        <!-- Reviews -->
        <CCol class="widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Reviews"
            :text= String(this.item.restaurant_reviews_count)
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-star" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <CLink
                  :to="{ name: 'RestaurantReviews', params: { restaurant_id: item.id } }"
                  class="font-sm text-muted d-flex justify-content-between"
                  target="_self"
                >
                  Show Reviews
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>


        <!-- Zip Codes -->
        <CCol class="widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Zip Codes"
            :text= String(this.item.zip_codes_linked_restaurants_count)
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-location-pin" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <CLink
                  :to="{ name: 'RestaurantLinkedZIPCodes', params: { restaurant_id: item.id } }"
                  class="font-sm text-muted d-flex justify-content-between"
                  target="_self"
                >
                  Show Zip Codes
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>


        <!-- Linked Types -->
        <CCol class="widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Linked Types"
            :text= String(this.item.restaurant_linked_types_count)
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-text" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <CLink
                  :to="{ name: 'RestaurantLinkedTypes', params: { restaurant_id: item.id } }"
                  class="font-sm text-muted d-flex justify-content-between"
                  target="_self"
                >
                  Show Linked Types
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>


        <!-- Linked Categories -->
        <CCol class="widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Categories"
            :text= String(this.item.categories_count)
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-folder" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <CLink
                  :to="{ name: 'RestaurantLinkedCategories', params: { restaurant_id: item.id } }"
                  class="font-sm text-muted d-flex justify-content-between"
                  target="_self"
                >
                  Show Categories
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>


        <!-- Linked Menus -->
        <CCol class="widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Menus"
            :text= String(this.item.menus_count)
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-dinner" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <CLink
                  :to="{ name: 'RestaurantMenuV2List', params: { restaurant_id: item.id } }"
                  class="font-sm text-muted d-flex justify-content-between"
                  target="_self"
                >
                  Show Menus
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>


        <!-- Menu Sjabloons -->
        <!-- <CCol class="widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Menu Sjabloons"
            :text= String(this.item.menu_sjabloons_count)
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-object-ungroup" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <CLink
                  :to="{ name: 'Restaurant Menu Sjabloons', params: { restaurant_id: item.id } }"
                  class="font-sm text-muted d-flex justify-content-between"
                  target="_self"
                >
                  Show Menu Sjabloons
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol> -->


        <!-- Tasks -->
        <CCol class="widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Tasks"
            :text= "`${this.item.open_tasks_count} / ${this.item.tasks_count}`"
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-bolt" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <CLink
                  :to="{ name: 'RestaurantTaskList', params: { restaurant_id: item.id } }"
                  class="font-sm text-muted d-flex justify-content-between"
                  target="_self"
                >
                  Show Tasks
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>


        <!-- Permissions -->
        <CCol class="widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Permissions"
            :text="`${this.item.permissions_count}`"
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-lock-unlocked" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <CLink
                  :to="{ name: 'RestaurantPermissionList', params: { restaurant_id: item.id } }"
                  class="font-sm text-muted d-flex justify-content-between"
                  target="_self"
                >
                  Show Permissions
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>

        <!-- Revenues  -->
        <CCol class="widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Revenues"
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-bar-chart" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <CLink
                  :to="{ name: 'Revenue', query: { r: item.id } }"
                  class="font-sm text-muted d-flex justify-content-between"
                  target="_self"
                >
                  Show Revenues
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>

        <!-- Device Link -->
        <CCol class="device-widget widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="Device"
            :text=textLinkedDevice
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-mobile" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <div
                  v-if="linkedDevice"
                  class="px-1 pb-2 text-left"
                >
                  <div class="d-flex justify-content-between align-items-center">
                    <span class="small text-muted">Status</span>
                    <CBadge :color="linkedDeviceStatusColor">
                      {{ linkedDeviceStatusText }}
                    </CBadge>
                  </div>
                  <div class="small text-muted mt-1">
                    {{ linkedDeviceLastCheckText }}
                  </div>
                  <div class="small text-muted">
                    {{ linkedDeviceTypeText }}
                    <span v-if="linkedDeviceVersion">- v{{ linkedDeviceVersion }}</span>
                  </div>
                </div>

                <CLink
                  v-if="linkedDevice && linkedDeviceId"
                  :to="{ name: 'Edit Device', params: { id: linkedDeviceId } }"
                  class="font-sm text-muted d-flex justify-content-between align-items-center px-1 pb-2"
                >
                  Open Device #{{ linkedDeviceId }}
                  <CIcon name="cil-arrow-right" width="16"/>
                </CLink>

                <CButton
                  v-if="linkedDevice"
                  color="info"
                  size="sm"
                  shape="pill"
                  class="mr-1 mb-1"
                  :disabled="sendingTerminalUpdate"
                  @click="sendTerminalUpdate"
                >
                  <CSpinner v-if="sendingTerminalUpdate" size="sm" />
                  <CIcon v-else name="cil-sync" />
                  Send Update
                </CButton>

                <CButton
                  v-if="linkedDevice"
                  color="danger"
                  size="sm"
                  shape="pill"
                  class="mb-1"
                  @click="unlinkDevice"
                >
                  <CIcon name="cil-x" /> Unlink Device
                </CButton>

                <CTabs v-else :active-tab.sync="activeTab">
                  <CTab v-if="previousRestDevice" title="Get From the Previous" class="tab-item">
                    <ul class="pt-2">
                      <li>Previous Restaurant ID: #{{ previousRestDevice.restaurant_id }}</li>
                      <li>Device SID: {{ previousRestDevice.device?.sid }} (#{{ previousRestDevice.device_id }})</li>
                      <li>Software: {{ previousRestDevice.device?.software ? 'YES' : 'NO' }}</li>
                    </ul>

                    <CButton
                      type="submit"
                      color="success"
                      size="sm"
                      shape="pill"
                      @click="linkDevice($event, previousRestDevice)"
                    >
                      <CIcon name="cil-pin" /> Link Device
                    </CButton>
                  </CTab>

                  <CTab title="Select New" class="tab-item">
                    <CForm
                      class="pt-2"
                      @submit="linkDevice"
                    >
                      <v-select
                        class="mb-2"
                        v-model="selectedDevice"
                        :options="availableDevices"
                        placeholder="Please choose a device.."
                        :searchable="true"
                      >
                        <template #search="{attributes, events}">
                          <input
                            class="vs__search"
                            :required="!selectedDevice"
                            v-bind="attributes"
                            v-on="events"
                          />
                        </template>
                      </v-select>

                      <CButton
                        type="submit"
                        color="success"
                        size="sm"
                        shape="pill"
                      >
                        <CIcon name="cil-pin" /> Link Device
                      </CButton>
                    </CForm>
                  </CTab>
                </CTabs>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>

        <!-- MyRestaurant FCM Devices -->
        <CCol class="fcm-device-widget widget" col="12" sm="6" xl="3">
          <CWidgetIcon
            header="MyRestaurant FCM"
            :text="fcmDeviceWidgetText"
            color="gradient-dark"
            :icon-padding="false"
          >
            <CIcon name="cil-bell" class="" width="24" />
            <template #footer>
              <CCardFooter>
                <div v-if="fcmRestaurantLoginDevices.length" class="px-1 pb-2 text-left">
                  <div
                    v-for="device in fcmRestaurantLoginDevices"
                    :key="device.id"
                    class="py-1 border-bottom"
                  >
                    <div class="d-flex justify-content-between align-items-center flex-gap-1">
                      <strong class="small">
                        #{{ device.id }} {{ fcmDeviceLabel(device) }}
                      </strong>
                      <CBadge :color="device.is_notify ? 'success' : 'warning'">
                        {{ device.is_notify ? 'Notify on' : 'Notify off' }}
                      </CBadge>
                    </div>
                    <div class="small text-muted">{{ fcmDeviceMeta(device) }}</div>
                    <div class="small text-muted">{{ fcmDeviceRuntime(device) }}</div>
                    <div class="small text-muted">{{ fcmDeviceApp(device) }}</div>
                    <div v-if="fcmDeviceFlags(device)" class="small text-muted">{{ fcmDeviceFlags(device) }}</div>
                    <div class="small text-muted">{{ fcmDeviceLastActive(device) }}</div>
                    <div class="small text-muted">{{ fcmDeviceCreatedUpdated(device) }}</div>
                    <div v-if="device.last_unsubscribed" class="small text-muted">
                      Unsubscribed: {{ fcmDeviceDate(device.last_unsubscribed) }}
                    </div>
                    <div class="small text-muted">{{ fcmDeviceTokenLabel(device) }}</div>
                  </div>
                </div>

                <div v-else class="small text-muted px-1 pb-2 text-left">
                  No fcm-restaurant-login device linked.
                </div>
              </CCardFooter>
            </template>
          </CWidgetIcon>
        </CCol>
      </CRow>

      <div class="restaurant-history-panel mb-4">
        <div class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-2 flex-gap-2">
          <h5 class="mb-0">
            <CIcon name="cil-fingerprint" class="mr-1" />
            History
          </h5>
          <CButton
            color="light"
            size="sm"
            @click="showLatestActivities"
          >
            <CIcon name="cil-list" class="mr-1" />
            Show all
          </CButton>
        </div>

        <CAlert v-if="latestActivitiesError" color="danger" class="mb-0">
          {{ latestActivitiesError }}
        </CAlert>

        <div v-else-if="latestActivitiesLoading" class="text-muted py-3">
          <CSpinner size="sm" class="mr-2" />
          Loading history
        </div>

        <CListGroup v-else flush>
          <CListGroupItem v-if="!latestActivities.length" class="text-muted">
            No history yet.
          </CListGroupItem>

          <CListGroupItem
            v-for="activity in latestActivities"
            :key="activity.id"
            class="px-0"
          >
            <div class="d-flex flex-column flex-md-row justify-content-between flex-gap-2">
              <div>
                <strong>{{ activity.event || 'activity' }}</strong>
                <CBadge
                  v-if="activityOrderLabel(activity)"
                  color="info"
                  class="ml-2"
                >
                  {{ activityOrderLabel(activity) }}
                </CBadge>
                <div class="small text-muted mt-1">{{ activity.description }}</div>
                <div
                  v-for="change in activityChanges(activity)"
                  :key="change.attribute"
                  class="small mt-1 restaurant-history-change"
                >
                  <strong>{{ activityAttributeLabel(change.attribute) }}:</strong>
                  <template v-if="change.hasOld">
                    <span class="text-muted ml-1 text-break">{{ activityChangeValue(change.attribute, change.old) }}</span>
                    <CIcon name="cil-arrow-right" class="mx-1" />
                  </template>
                  <span class="text-success text-break">{{ activityChangeValue(change.attribute, change.new) }}</span>
                </div>
                <div v-if="activityMessage(activity)" class="small text-muted mt-1">
                  <CIcon name="cil-bell" class="mr-1" />
                  {{ activityMessage(activity) }}
                </div>
                <div v-if="activityCauserLabel(activity)" class="small text-muted mt-1">
                  <CIcon name="cil-user" class="mr-1" />
                  {{ activityCauserLabel(activity) }}
                </div>
              </div>
              <div class="small text-muted text-md-right flex-shrink-0">
                <CIcon name="cil-calendar" class="mr-1" />
                {{ moment(activity.created_at).calendar() }}
              </div>
            </div>
          </CListGroupItem>
        </CListGroup>
      </div>
    </CCardBody>

    <CElementCover :opacity="0.4" v-show="loading"/>

    <ChangeHeroImage :imageId="item.hero_image?.id" @update="changedHeroImage" />
    <ChangeLogo :restaurantId="this.item.id" :slug="this.item.restaurant_slug" :logo="getValidImageUrl(this.item.restaurant_logo_path)" @update="changedlogo" />
    <SendRestaurantDetails :restaurantId="item.id" :emails="uniqueEmails" :detail="detailToSend" />
    <LatestActivities :restaurant="{ id: item.id, restaurant_name: item.restaurant_name, restaurant_postcode: item.restaurant_postcode }" />
  </CCard>

  <div v-else>
    <CSpinner v-if="loading" color="primary" />
    <h6 v-else>Restaurant not found!</h6>
  </div>
</div>
</template>

<script>
import { mapGetters } from "vuex";
import ChangeHeroImage from "@/components/Restaurant/ChangeHeroImage";
import ChangeLogo from "@/components/Restaurant/ChangeLogo";
import SendRestaurantDetails from "@/components/Restaurant/SendRestaurantDetails";
import LatestActivities from "./LatestActivities";
import CrmReports from "@/views/crm/reports/CrmReports";

export default {
  name: 'ViewRestaurant',
  components: {
    UnsignedContract: () => import('@/views/restaurants/sign-contract/UnsignedContract.vue'),
    ChangeHeroImage,
    ChangeLogo,
    SendRestaurantDetails,
    LatestActivities,
    CrmReports
  },

  data() {
    return {
      loading: false,
      item: null,
      linkedDevice: null,
      linkedDeviceId: null,
      savingInternalNote: false,
      creatingInternalNoteTask: false,
      loadingAdminUsers: false,
      adminUsers: [],
      loadingRepresentatives: false,
      savingRepresentative: false,
      assignedRepresentatives: [],
      representativeUsers: [],
      selectedRepresentative: '',
      internalNoteTaskAssigneeId: 'rest_admin',
      sendingTerminalUpdate: false,
      latestActivitiesLoading: false,
      latestActivities: [],
      latestActivitiesError: null,
      loadingTodayCustomOpeningTimes: false,
      todayCustomOpeningTimes: [],
      todayCustomOpeningTimesError: null,

      // Alert işlemleri için
      successAlertMessage: {},
      dismissSecs: 10,
      dismissSuccessAlert: 0,
      errorAlertMessage: {},
      showErrorAlert: false,

      uniqueEmails: [],
      availableDevices: [],
      availableDeviceIds: {},
      availableDeviceMeta: {},
      selectedDevice: null,
      activeTab: 0,
      detailToSend: null,
      slugForm: {
        restaurant_slug: '',
        saving: false,
      },
      startdateForm: {
        startdate: null,
        saving: false,
      },
      domainForm: {
        website_etenonline_be: '',
        subsites: [],
        saving: false,
        snapshot: '',
      },

      // Get previous page
      prevRoute: null,
    };
  },

  // Get previous page
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },

  watch: {
    async itemId() {
      await this.getValues();
    },
  },

  computed: {
    ...mapGetters(["errors"]),

    itemId() { return this.$route.params.id },
    currentUserRoles() {
      const userData = this.$store.getters['auth/userData'] || JSON.parse(localStorage.getItem('authData') || '{}');
      return (userData.roles || []).map(role => role.name || role);
    },
    canManageRepresentativeAccess() {
      return this.currentUserRoles.some(role => ['SuperAdmin', 'Admin', 'RestAdmin'].includes(role));
    },
    canManageSubsites() {
      return this.currentUserRoles.some(role => ['SuperAdmin', 'Admin'].includes(role));
    },
    assignedRepresentativeIds() {
      return (this.assignedRepresentatives || []).map(representative => Number(representative.id));
    },
    availableRepresentativeOptions() {
      const assignedIds = new Set(this.assignedRepresentativeIds);
      return (this.representativeUsers || [])
        .map(this.normalizeRepresentativeOption)
        .filter(representative => representative.id && !assignedIds.has(Number(representative.id)))
        .map(representative => ({
          value: representative.id,
          label: representative.email
            ? `${this.representativeDisplayName(representative)} (${representative.email})`
            : this.representativeDisplayName(representative),
        }));
    },
    representativeSelectOptions() {
      return [
        { value: '', label: 'Select representative' },
        ...this.availableRepresentativeOptions,
      ];
    },
    adminUserOptions() {
      return [
        { value: 'rest_admin', label: 'Auto assign RestAdmin' },
        { value: 'order_admin', label: 'Auto assign OrderAdmin' },
        ...(this.adminUsers || []),
      ];
    },
    previousRestaurantId() {
      return this.getRelatedRestaurantId(this.item?.prev_rest);
    },
    nextRestaurantId() {
      return this.getRelatedRestaurantId(this.item?.next_rest);
    },
    previousRestaurantLabel() {
      return this.getRelatedRestaurantLabel(this.item?.prev_rest);
    },
    nextRestaurantLabel() {
      return this.getRelatedRestaurantLabel(this.item?.next_rest);
    },
    hasRelatedRestaurants() {
      return Boolean(this.previousRestaurantId || this.nextRestaurantId);
    },
    textLinkedDevice() { return this.linkedDevice ? this.linkedDevice : "No device linked yet!" },
    linkedDeviceRelation() {
      return this.item?.restaurant_device_link_with_device || null;
    },
    linkedDeviceInfo() {
      return this.linkedDeviceRelation?.device || null;
    },
    linkedDeviceStatusText() {
      if (Number(this.linkedDeviceInfo?.ws_status) === 1) {
        return 'Online';
      }

      if (this.item?._check_connection) {
        return 'Recent check-in';
      }

      if (this.linkedDeviceRelation) {
        return 'Offline';
      }

      return 'Not linked';
    },
    linkedDeviceStatusColor() {
      if (Number(this.linkedDeviceInfo?.ws_status) === 1 || this.item?._check_connection) {
        return 'success';
      }

      return this.linkedDeviceRelation ? 'danger' : 'secondary';
    },
    linkedDeviceLastCheckText() {
      if (!this.linkedDeviceRelation) {
        return 'No device linked yet.';
      }

      if (!this.linkedDeviceRelation.lastcheck) {
        return 'No last check yet.';
      }

      const suffix = Number(this.linkedDeviceInfo?.ws_status) === 1 ? ' from WS' : '';
      return `Last check: ${this.item?._hr_datetime || this.linkedDeviceRelation.lastcheck}${suffix}`;
    },
    linkedDeviceTypeText() {
      if (!this.linkedDeviceInfo) {
        return 'Device type: unknown';
      }

      return `Device type: ${this.linkedDeviceInfo.software ? 'MS' : 'MB'}`;
    },
    linkedDeviceVersion() {
      return this.linkedDeviceInfo?.version || null;
    },
    fcmRestaurantLoginDevices() {
      return this.item?.fcm_restaurant_login_devices || [];
    },
    weekdaySchedules() {
      return [
        { day: 0, label: 'Sunday', openField: 'open_sunday', deliveryField: 'deliver_sunday' },
        { day: 1, label: 'Monday', openField: 'open_monday', deliveryField: 'deliver_monday' },
        { day: 2, label: 'Tuesday', openField: 'open_tuesday', deliveryField: 'deliver_tuesday' },
        { day: 3, label: 'Wednesday', openField: 'open_wednesday', deliveryField: 'deliver_wednesday' },
        { day: 4, label: 'Thursday', openField: 'open_thursday', deliveryField: 'deliver_thursday' },
        { day: 5, label: 'Friday', openField: 'open_friday', deliveryField: 'deliver_friday' },
        { day: 6, label: 'Saturday', openField: 'open_saturday', deliveryField: 'deliver_saturday' },
      ];
    },
    todayDate() {
      return this.moment().format('YYYY-MM-DD');
    },
    todaySchedule() {
      const today = new Date().getDay();

      return this.weekdaySchedules.find((schedule) => schedule.day === today) || this.weekdaySchedules[0];
    },
    todayScheduleLabel() {
      return `${this.todaySchedule.label} (${this.todayDate})`;
    },
    todayOpeningField() {
      return this.todaySchedule.openField;
    },
    todayDeliveryField() {
      return this.todaySchedule.deliveryField;
    },
    todayRawOpeningTimes() {
      return this.rawTimeValue(this.item?.[this.todaySchedule.openField]);
    },
    todayRawDeliveryTimes() {
      return this.rawTimeValue(this.item?.[this.todaySchedule.deliveryField]);
    },
    fcmRestaurantLoginDevicesCount() {
      return Number(this.item?.fcm_restaurant_login_devices_count || this.fcmRestaurantLoginDevices.length || 0);
    },
    fcmDeviceWidgetText() {
      const count = this.fcmRestaurantLoginDevicesCount;

      return count > 0 ? `${count} device${count === 1 ? '' : 's'}` : 'No FCM device';
    },
    averageDeliveryDistance() {
      return this.item?.average_delivery_distance || {};
    },
    averageDeliveryDistanceWidgetText() {
      return `Avg. ${this.formatDeliveryDistance(this.averageDeliveryDistance.kilometers)}`;
    },
    maximumDeliveryDistanceWidgetText() {
      return `Max. ${this.formatDeliveryDistance(this.averageDeliveryDistance.maximum_kilometers)}`;
    },
    averageDeliveryDistanceOrdersText() {
      const count = Number(this.averageDeliveryDistance.orders_count || 0);

      if (!count) {
        return 'No delivery distance data';
      }

      return `Based on ${count} delivery order${count === 1 ? '' : 's'}`;
    },
    storedLang() {
      return this.item?.lang ? String(this.item.lang).toLowerCase() : null;
    },
    derivedLang() {
      return this.item?.derived_lang ? String(this.item.derived_lang).toLowerCase() : null;
    },
    hasGeoMetadata() {
      return Boolean(this.item?.province || this.item?.region || this.derivedLang);
    },
    geoProvinceName() {
      return this.item?.province?.name_nl || this.item?.province?.code || 'Not resolved';
    },
    geoRegionName() {
      return this.item?.region?.name_nl || this.item?.region?.code || 'Not resolved';
    },
    hasLanguageMismatch() {
      return Boolean(this.storedLang && this.derivedLang && this.storedLang !== this.derivedLang);
    },
    currentStartdate() {
      return this.normalizeDateInput(this.item?.startdate);
    },
    formattedStartdate() {
      return this.currentStartdate
        ? this.moment(this.currentStartdate).format('LL')
        : 'Not set';
    },
    canSaveStartdate() {
      return this.normalizeDateInput(this.startdateForm.startdate) !== this.currentStartdate;
    },
    canSaveSlug() {
      const nextSlug = String(this.slugForm.restaurant_slug || '').trim();
      const currentSlug = String(this.item?.restaurant_slug || '');

      return nextSlug.length >= 3 && nextSlug !== currentSlug;
    },
    canSaveSubsites() {
      return this.subsiteSnapshot(this.domainFormPayload()) !== this.domainForm.snapshot;
    },
    previousRestDevice() {
      const { previous } = this.item;
      const linkWithDevice = previous?.restaurant_device_link_with_device;

      if (linkWithDevice && !linkWithDevice.device.software) {
        return linkWithDevice;
      }

      return null;
    }
  },

  async mounted() {
    this.getAvailableDevices();
    await Promise.all([
      this.getValues(),
      this.getAdminUsers(),
      this.canManageRepresentativeAccess ? this.getRepresentativeUsers() : Promise.resolve(),
    ]);
  },

  methods: {
    formatErrorMessage(error, fallback = 'Something went wrong.') {
      const message = error?.response?.data?.message;

      if (typeof message === 'string') {
        return message;
      }

      if (message && typeof message === 'object') {
        return Object.values(message).flat().join(' ');
      }

      return fallback;
    },
    async getAdminUsers() {
      const cacheKey = 'admin-users-excluding-current';
      const cachedUsers = sessionStorage.getItem(cacheKey);

      if (cachedUsers) {
        this.adminUsers = JSON.parse(cachedUsers);
        return;
      }

      this.loadingAdminUsers = true;
      try {
        const response = await this.$axios.get(this.$backend.USERS.GET_ADMIN_USERS, {
          params: { 'exclude-user': 'current' },
        });
        this.adminUsers = response.data;
        sessionStorage.setItem(cacheKey, JSON.stringify(this.adminUsers));
      } catch (error) {
        this.adminUsers = [];
      } finally {
        this.loadingAdminUsers = false;
      }
    },
    async getRepresentativeUsers() {
      const cacheKey = 'representative-users';
      const cachedUsers = sessionStorage.getItem(cacheKey);

      if (cachedUsers) {
        this.representativeUsers = JSON.parse(cachedUsers);
        return;
      }

      try {
        const response = await this.$axios.get(this.$backend.USERS.GET_ADMIN_USERS, {
          params: { role: 'Representative', fields: 'last_name,email' },
        });
        this.representativeUsers = (response.data || []).map(this.normalizeRepresentativeOption);
        sessionStorage.setItem(cacheKey, JSON.stringify(this.representativeUsers));
      } catch (error) {
        this.representativeUsers = [];
      }
    },
    normalizeRepresentativeOption(representative) {
      if (!representative) {
        return {};
      }

      return {
        ...representative,
        id: Number(representative.id || representative.value),
        first_name: representative.first_name || representative.label || '',
        last_name: representative.last_name || '',
        email: representative.email || '',
      };
    },
    representativeDisplayName(representative) {
      const normalized = this.normalizeRepresentativeOption(representative);
      return [normalized.first_name, normalized.last_name].filter(Boolean).join(' ')
        || normalized.email
        || `#${normalized.id}`;
    },
    async getRepresentatives() {
      if (!this.canManageRepresentativeAccess || !this.itemId) {
        return;
      }

      this.loadingRepresentatives = true;
      try {
        const response = await this.$axios.get(
          this.$backend.RESTAURANT.REPRESENTATIVES.GET_ALL.replace('{id}', this.itemId)
        );
        this.assignedRepresentatives = (response.data || []).map(this.normalizeRepresentativeOption);
      } catch (error) {
        this.assignedRepresentatives = [];
        this.$toast.error(this.formatErrorMessage(error, 'Failed to load representatives.'));
      } finally {
        this.loadingRepresentatives = false;
      }
    },
    async assignRepresentative() {
      const userId = Number(this.selectedRepresentative);
      if (!userId) {
        return;
      }

      this.savingRepresentative = true;
      try {
        const response = await this.$axios.post(
          this.$backend.RESTAURANT.REPRESENTATIVES.STORE.replace('{id}', this.itemId),
          { user_id: userId }
        );
        const assignedRepresentative = this.normalizeRepresentativeOption(response.data?.data);
        this.assignedRepresentatives = [
          ...this.assignedRepresentatives.filter(representative => Number(representative.id) !== assignedRepresentative.id),
          assignedRepresentative,
        ];
        this.selectedRepresentative = '';
        this.$toast.success(response.data?.message || 'Representative assigned.');
      } catch (error) {
        this.$toast.error(this.formatErrorMessage(error, 'Failed to assign representative.'));
      } finally {
        this.savingRepresentative = false;
      }
    },
    removeRepresentative(representative) {
      const normalized = this.normalizeRepresentativeOption(representative);
      this.$confirm({
        title: 'Confirm',
        message: `Remove representative access for ${this.representativeDisplayName(normalized)}?`,
        button: { no: 'No', yes: 'Yes' },
        callback: async (confirm) => {
          if (!confirm) {
            return;
          }

          this.savingRepresentative = true;
          try {
            await this.$axios.delete(
              this.$backend.RESTAURANT.REPRESENTATIVES.DELETE
                .replace('{id}', this.itemId)
                .replace('{user_id}', normalized.id)
            );
            this.assignedRepresentatives = this.assignedRepresentatives
              .filter(item => Number(item.id) !== normalized.id);
            this.$toast.success('Representative removed.');
          } catch (error) {
            this.$toast.error(this.formatErrorMessage(error, 'Failed to remove representative.'));
          } finally {
            this.savingRepresentative = false;
          }
        },
      });
    },
    formatDeliveryDistance(kilometers) {
      const value = Number(kilometers);

      return kilometers === null || kilometers === undefined || Number.isNaN(value)
        ? '-'
        : `${value.toFixed(1)} km`;
    },
    getRelatedRestaurantId(restaurant) {
      if (!restaurant) {
        return null;
      }

      if (typeof restaurant === 'object') {
        return restaurant.id || restaurant.restaurant_id || null;
      }

      return restaurant;
    },
    getRelatedRestaurantLabel(restaurant) {
      if (!restaurant) {
        return '';
      }

      const restaurantId = this.getRelatedRestaurantId(restaurant);

      if (typeof restaurant === 'object' && restaurant.restaurant_name) {
        return `${restaurant.restaurant_name} (#${restaurantId})`;
      }

      return `#${restaurantId}`;
    },
    langBadgeColor(lang) {
      switch (lang) {
        case 'nl':
          return 'success';
        case 'fr':
          return 'info';
        case 'de':
          return 'warning';
        default:
          return 'secondary';
      }
    },

    normalizeDateInput(value) {
      return value ? String(value).slice(0, 10) : null;
    },
    rawTimeValue(value) {
      if (value === null || typeof value === 'undefined' || String(value).trim() === '') {
        return '(empty)';
      }

      return String(value);
    },
    async getValues() {
      this.loading = true;
      this.todayCustomOpeningTimes = [];
      this.todayCustomOpeningTimesError = null;
      await this.$axios.get(this.$backend.RESTAURANT.SHOW.replace("{id}", this.itemId))
        .then((response) => {
          const linkedDevice = response.data.restaurant_device_link_with_device;
          this.item = response.data;
          this.slugForm.restaurant_slug = response.data.restaurant_slug || '';
          this.startdateForm.startdate = this.normalizeDateInput(response.data.startdate);
          this.initializeDomainForm();
          this.linkedDevice = linkedDevice?.device?.sid || null;
          this.linkedDeviceId = linkedDevice?.device_id || linkedDevice?.device?.id || null;
          this.getTodayCustomOpeningTimes();
          this.getLatestRestaurantActivities();
          if (this.canManageRepresentativeAccess) {
            this.getRepresentatives();
          }
        })
        .catch((error) => {
          this.errorAlertMessage = { 'Error': error.response.data.message }
          this.showErrorAlert = true
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async getTodayCustomOpeningTimes() {
      if (!this.item?.id) {
        return;
      }

      this.loadingTodayCustomOpeningTimes = true;
      this.todayCustomOpeningTimesError = null;

      try {
        const { data } = await this.$axios.get(
          this.$backend.RESTAURANT.OPENING_TIMES.GET_ALL.replace("{restaurant_id}", this.item.id),
          {
            params: {
              date: this.todayDate,
            },
          }
        );

        this.todayCustomOpeningTimes = (data || []).filter((customTime) => {
          return this.normalizeDateInput(customTime.custom_date) === this.todayDate;
        });
      } catch (error) {
        this.todayCustomOpeningTimes = [];
        this.todayCustomOpeningTimesError = error.response?.data?.message || "Today's custom opening time could not be loaded.";
      } finally {
        this.loadingTodayCustomOpeningTimes = false;
      }
    },

    async getLatestRestaurantActivities() {
      if (!this.item?.id) {
        return;
      }

      this.latestActivitiesLoading = true;
      this.latestActivitiesError = null;

      try {
        const { data } = await this.$axios.get(this.$backend.ACTIVITIES.GET_LATEST_ACTIVITIES, {
          params: {
            log_name: 'restaurant',
            subject: this.item.id,
            top: 20,
          },
        });

        this.latestActivities = data.data || data || [];
      } catch (error) {
        this.latestActivitiesError = error.response?.data?.message || 'History could not be loaded.';
      } finally {
        this.latestActivitiesLoading = false;
      }
    },

    activityCauserLabel(activity) {
      const causer = activity?.causer;

      if (!causer) {
        return null;
      }

      if (causer.restaurant_name) {
        return causer.restaurant_name;
      }

      return [causer.first_name, causer.last_name].filter(Boolean).join(' ') || null;
    },

    activityOrderLabel(activity) {
      const roStrId = activity?.properties?.roStrId;

      return roStrId ? `Order #${roStrId}` : null;
    },

    activityMessage(activity) {
      return activity?.properties?.message || activity?.properties?.title || null;
    },

    activityChanges(activity) {
      if (activity?.event !== 'updated') {
        return [];
      }

      const attributes = activity?.properties?.attributes;
      const old = activity?.properties?.old || {};

      if (!attributes || typeof attributes !== 'object' || Array.isArray(attributes)) {
        return [];
      }

      return Object.keys(attributes)
        .filter(attribute => !['created_at', 'updated_at', 'password', 'remember_token'].includes(attribute))
        .map(attribute => ({
          attribute,
          old: old[attribute],
          new: attributes[attribute],
          hasOld: Object.prototype.hasOwnProperty.call(old, attribute),
        }));
    },

    activityAttributeLabel(attribute) {
      const labels = {
        restaurant_name: 'Restaurant name',
        restaurant_address: 'Address',
        restaurant_postcode: 'Postcode',
        restaurant_city: 'City',
        restaurant_tel: 'Telephone',
        restaurant_gsm: 'Mobile phone',
        restaurant_description: 'Description',
        restaurant_slug: 'Slug',
        none_active: 'Published',
        relation_active: 'Relation active',
        menu_active: 'Menu active',
        temp_closed: 'Temporarily closed',
        onlytakeaway: 'Takeaway only',
        onlyonlinepay: 'Online payment only',
        hub_delivery_disabled: 'Hub delivery disabled',
      };

      return labels[attribute] || attribute
        .replace(/_/g, ' ')
        .replace(/\b\w/g, letter => letter.toUpperCase());
    },

    activityChangeValue(attribute, value) {
      if (value === null || value === undefined || value === '') {
        return 'Empty';
      }

      const booleanAttributes = [
        'menu_active',
        'relation_active',
        'none_active',
        'temp_closed',
        'onlytakeaway',
        'onlyonlinepay',
        'hub_delivery_disabled',
        'rest_closed_today',
        'exclusive',
        'gpweb',
      ];

      if (booleanAttributes.includes(attribute) && [0, 1, '0', '1', false, true].includes(value)) {
        return value === 1 || value === '1' || value === true ? 'Yes' : 'No';
      }

      if (typeof value === 'object') {
        return JSON.stringify(value);
      }

      return String(value);
    },

    fcmDeviceLabel(device) {
      return device?.device_model || device?.device_type || 'Device';
    },

    fcmDeviceMeta(device) {
      return [device?.manufacturer, device?.device_os, device?.device_type]
        .filter(Boolean)
        .join(' / ') || 'No device metadata';
    },
    initializeDomainForm() {
      const payload = {
        website_etenonline_be: this.item?.website_etenonline_be || '',
        subsites: (this.item?.subsites || []).map(subsite => ({
          domain: subsite?.domain || '',
        })),
      };

      this.domainForm.website_etenonline_be = payload.website_etenonline_be;
      this.domainForm.subsites = payload.subsites;
      this.domainForm.snapshot = this.subsiteSnapshot(payload);
    },
    domainFormPayload() {
      return {
        website_etenonline_be: this.domainForm.website_etenonline_be || null,
        subsites: (this.domainForm.subsites || [])
          .map(subsite => ({ domain: (subsite.domain || '').trim() }))
          .filter(subsite => subsite.domain !== ''),
      };
    },
    subsiteSnapshot(payload) {
      return JSON.stringify({
        website_etenonline_be: payload.website_etenonline_be || null,
        subsites: (payload.subsites || []).map(subsite => subsite.domain || ''),
      });
    },
    addSubsiteDomain() {
      this.domainForm.subsites.push({ domain: '' });
    },
    removeSubsiteDomain(index) {
      this.domainForm.subsites.splice(index, 1);
    },
    async saveSubsites() {
      this.domainForm.saving = true;

      try {
        const payload = this.domainFormPayload();
        const { data } = await this.$axios.patch(
          this.$backend.RESTAURANT.SUBSITES.replace('{id}', this.item.id),
          payload
        );

        this.item.website_etenonline_be = data.website_etenonline_be;
        this.item.subsites = data.subsites || [];
        this.item.dns_check_result = data.dns_check_result;
        this.initializeDomainForm();
        this.$toast.success('Subsites/domains saved.');
      } catch (error) {
        this.$toast.error(this.formatErrorMessage(error, 'Failed to save subsites/domains.'));
      } finally {
        this.domainForm.saving = false;
      }
    },

    fcmDeviceRuntime(device) {
      return [
        `Sessions: ${Number(device?.sessions || 0)}`,
        device?.lang ? `Lang: ${device.lang}` : null,
        device?.country ? `Country: ${device.country}` : null,
        device?.timezone ? `TZ: ${device.timezone}` : null,
        device?.app_versions ? `Version: ${device.app_versions}` : null,
      ].filter(Boolean).join(' | ');
    },

    fcmDeviceApp(device) {
      return [
        device?.app?.app_id,
        device?.app?.provider,
        device?.app?.domain,
      ].filter(Boolean).join(' / ') || 'No app metadata';
    },

    fcmDeviceFlags(device) {
      return [
        Object.prototype.hasOwnProperty.call(device || {}, 'isDesktop') ? `Desktop: ${device.isDesktop ? 'yes' : 'no'}` : null,
        Object.prototype.hasOwnProperty.call(device || {}, 'is_rooted') ? `Rooted: ${device.is_rooted ? 'yes' : 'no'}` : null,
        Object.prototype.hasOwnProperty.call(device || {}, 'onesignal') ? `OneSignal: ${device.onesignal ? 'yes' : 'no'}` : null,
      ].filter(Boolean).join(' | ');
    },

    fcmDeviceDate(value) {
      return value ? this.moment(value).calendar() : '-';
    },

    fcmDeviceLastActive(device) {
      return device?.last_active
        ? `Last active: ${this.fcmDeviceDate(device.last_active)}`
        : 'No last active';
    },

    fcmDeviceCreatedUpdated(device) {
      return [
        `Created: ${this.fcmDeviceDate(device?.created_at)}`,
        `Updated: ${this.fcmDeviceDate(device?.updated_at)}`,
      ].join(' | ');
    },

    fcmDeviceTokenLabel(device) {
      if (!device?.device_id) {
        return 'No FCM token';
      }

      return `Token: ${String(device.device_id).slice(0, 14)}...`;
    },

    getValidImageUrl(url) {
      const ORIGIN_CDN = process.env.VUE_APP_ORIGIN_CDN;

      return url
        ? this.$globalFunc.isURL(url)
          ? url
          : ORIGIN_CDN + url
        : "";
    },

    async getAvailableDevices() {
      const response = await this.$axios.get(this.$backend.DEVICE.AVAILABLE_DEVICES);
      const devices = Object.keys(response.data).map((key) => response.data[key]);
      this.availableDevices = devices.map((device) => device.sid);
      this.availableDeviceIds = devices.reduce((carry, device) => {
        const deviceId = device?.id || device?.device_id || null;
        if (device?.sid && deviceId) {
          carry[device.sid] = deviceId;
        }
        return carry;
      }, {});
      this.availableDeviceMeta = devices.reduce((carry, device) => {
        if (device?.sid) {
          carry[device.sid] = device;
        }
        return carry;
      }, {});
    },

    async sendTerminalUpdate() {
      this.sendingTerminalUpdate = true;
      this.showErrorAlert = false;

      try {
        const { data } = await this.$axios.post(
          this.$backend.RESTAURANT.SEND_TERMINAL_UPDATE.replace("{id}", this.item.id)
        );

        const message = data.message || 'Terminal update sent successfully.';
        this.successAlertMessage = { itemName: 'Success:', message };
        this.dismissSuccessAlert = this.dismissSecs;
        this.showErrorAlert = false;
        this.$toast.success(message);
      } catch (error) {
        const message = error.response?.data?.message || 'Terminal update could not be sent.';
        this.errorAlertMessage = { 'Error': message };
        this.showErrorAlert = true;
        this.$toast.error(message);
      } finally {
        this.sendingTerminalUpdate = false;
      }
    },

    linkDevice(e, previousRestDevice = null) {
      e.preventDefault();
      this.loading = true;
      this.$axios
        .post(this.$backend.RESTAURANT.LINK_DEVICE, {
          restaurant_id: this.itemId,
          device_sid: previousRestDevice
            ? previousRestDevice.device?.sid
            : this.selectedDevice,
          previous_restaurant_id: previousRestDevice?.restaurant_id,
        })
        .then((response) => {
          this.selectedDevice = response.data.data.device_sid;

          this.availableDevices = this.availableDevices.filter(
            (item) => item !== this.selectedDevice
          );
          this.linkedDevice = this.selectedDevice;
          this.linkedDeviceId = response.data.data.device_id
            || response.data.data.id
            || previousRestDevice?.device_id
            || this.availableDeviceIds[this.selectedDevice]
            || null;
          const linkedDeviceMeta = previousRestDevice?.device || this.availableDeviceMeta[this.linkedDevice] || {};
          this.item.restaurant_device_link_with_device = {
            device_id: this.linkedDeviceId,
            lastcheck: previousRestDevice?.lastcheck || null,
            device: {
              id: this.linkedDeviceId,
              sid: this.linkedDevice,
              software: linkedDeviceMeta.software || false,
              ws_status: linkedDeviceMeta.ws_status || 0,
              version: linkedDeviceMeta.version || null,
            },
          };
        })
        .finally(() => {
          this.loading = false;
          this.getAvailableDevices(); // authored by Administrator
        });
    },

    unlinkDevice() {
      this.$confirm({
        title: 'Confirm',
        message: `⚠️ Do you really want to unlink the device?`,
        button: {
          no: "No",
          yes: "Yes",
        },
        /**
         * Callback Function
         * @param {Boolean} confirm
         */
        callback: (confirm) => {
          if (confirm) {
            // ---------------
            this.loading = true;
            this.$axios.post(this.$backend.RESTAURANT.UNLINK_DEVICE, { 'restaurant_id': this.itemId, 'device_sid': this.linkedDevice })
              .then(() => {
                this.availableDevices.push(this.linkedDevice);
                this.availableDevices.sort();

                this.selectedDevice = this.linkedDevice;
                this.linkedDevice = null;
                this.linkedDeviceId = null;
                this.item.restaurant_device_link_with_device = null;
              })
              .catch((error) => {
                this.errorAlertMessage = { 'Error': error.response.data.message }
                this.showErrorAlert = true
              })
              .finally(() => {
                this.loading = false;
                this.getAvailableDevices(); // authored by Administrator
              });
            // ---------------
          }
        },
      });
    },

    updateRestaurantStatus(none_active) {
      const isPublished = !none_active;

      if (isPublished) {
        if (this.item.next_rest) {
          this.$confirm({
            title: "Confirm",
            message: `⚠️ The restaurant has next restaurant. Do you want to publish this?`,
            button: { no: "No", yes: "Yes" },
            callback: (confirm) => {
              if (confirm) {
                this.updateActive();
              } else {
                this.item.none_active = !this.item.none_active;
                this.$store.commit('set', ['restaurantsAsideShow', true]);
              }
            },
          });
          return;
        }
        
        if (this.item.published_previous_count > 0) {
          this.$confirm({
            title: "Confirm",
            message: `⚠️ The restaurant has previous restaurant. Do you want to unpublish previous restaurant?`,
            button: { no: "No", yes: "Yes" },
            callback: this.updateActive,
          });
          return;
        }
      }

      this.updateActive();
    },
    updateActive(unpublishPrevious = false) {
      this.loading = true;

      this.$axios
        .post(this.$backend.RESTAURANT.PUBLISH.replace("{id}", this.item.id), {
          'none-active': this.item.none_active,
          'unpublish-previous': unpublishPrevious,
        })
        .catch((error) => {
          this.item.none_active = !this.item.none_active;
          this.$toast.error(`${error.response.data.message}`);
          this.errorAlertMessage = { 'Error': error.response.data.message }
          this.showErrorAlert = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async updateSettings(data) {
      this.loading = true;

      try {
        await this.$axios.put(this.$backend.RESTAURANT.UPDATE_SETTINGS.replace("{id}", this.item.id), data);
      } catch (error) {
        this.$toast.error(typeof this.errors === 'object'
          ? Object.values(this.errors).join()
          : this.errors
        );
      }

      this.loading = false;
    },

    async saveInternalNote() {
      this.savingInternalNote = true;

      try {
        const note = this.item.note_internal || null;
        await this.$axios.put(this.$backend.RESTAURANT.UPDATE_SETTINGS.replace("{id}", this.item.id), {
          note_internal: note,
        });
        this.item.note_internal = note;
        this.$toast.success('Internal note saved.');
      } catch (error) {
        this.$toast.error(error.response?.data?.message || 'Failed to save internal note.');
      } finally {
        this.savingInternalNote = false;
      }
    },

    async createInternalNoteTask() {
      const note = (this.item.note_internal || '').trim();

      if (!note) {
        this.$toast.error('Internal note is required to create a follow-up task.');
        return;
      }

      this.creatingInternalNoteTask = true;

      try {
        const payload = { note };
        if (this.internalNoteTaskAssigneeId === 'rest_admin' || this.internalNoteTaskAssigneeId === 'order_admin') {
          payload.assignedAdminRole = this.internalNoteTaskAssigneeId;
        } else if (this.internalNoteTaskAssigneeId) {
          payload.assignedAdminId = this.internalNoteTaskAssigneeId;
        }

        const { data } = await this.$axios.post(
          this.$backend.RESTAURANT.INTERNAL_NOTE_TASK.replace("{id}", this.item.id),
          payload
          );
          this.item.note_internal = data.note_internal;
          this.$toast.success('Follow-up task created.');
          this.showTaskWarnings(data.warnings);
          this.internalNoteTaskAssigneeId = 'rest_admin';
        } catch (error) {
          this.$toast.error(this.formatErrorMessage(error, 'Failed to create follow-up task.'));
      } finally {
          this.creatingInternalNoteTask = false;
        }
      },
      showTaskWarnings(warnings) {
        (warnings || []).forEach((warning) => {
          this.$toast.warning(warning.message || 'Task created with a warning.');
        });
      },

      async updateGpweb(gpweb) {
      const previousGpweb = !gpweb;
      this.loading = true;

      try {
        const { data } = await this.$axios.patch(
          this.$backend.RESTAURANT.UPDATE_GPWEB.replace("{id}", this.item.id),
          { gpweb }
        );

        this.item.gpweb = data.gpweb;
        this.item.gpweb_updated_at = data.gpweb_updated_at;
        this.$toast.success(`GPWEB updated to ${data.gpweb ? 'G1' : 'G0'}.`);
      } catch (error) {
        this.item.gpweb = previousGpweb;
        this.$toast.error(
          typeof this.errors === 'object'
            ? Object.values(this.errors).join()
            : this.errors
        );
      }

      this.loading = false;
    },

    confirmStartdateChange() {
      if (!this.canSaveStartdate) {
        return;
      }

      const format = (value) => value ? this.moment(value).format('LL') : 'not set';
      const currentStartdate = this.currentStartdate;
      const nextStartdate = this.normalizeDateInput(this.startdateForm.startdate);

      this.$confirm({
        title: 'Confirm start date change',
        message: `Change start date from "${format(currentStartdate)}" to "${format(nextStartdate)}"?`,
        button: { no: 'No', yes: 'Yes' },
        callback: (confirm) => {
          if (confirm) {
            this.updateStartdate();
          }
        },
      });
    },

    async updateStartdate() {
      const previousStartdate = this.currentStartdate;
      this.startdateForm.saving = true;

      try {
        const { data } = await this.$axios.patch(
          this.$backend.RESTAURANT.UPDATE_STARTDATE.replace("{id}", this.item.id),
          { startdate: this.normalizeDateInput(this.startdateForm.startdate) }
        );

        this.item.startdate = data.startdate;
        this.startdateForm.startdate = this.normalizeDateInput(data.startdate);
        this.$toast.success(data.changed ? 'Start date updated.' : 'Start date already up to date.');
      } catch (error) {
        this.startdateForm.startdate = previousStartdate;
        const validationError = error.response?.data?.errors?.startdate;
        this.$toast.error(
          Array.isArray(validationError)
            ? validationError.join(', ')
            : error.response?.data?.message || 'Failed to update start date.'
        );
      } finally {
        this.startdateForm.saving = false;
      }
    },
    confirmSlugChange() {
      const nextSlug = String(this.slugForm.restaurant_slug || '').trim();
      const currentSlug = String(this.item?.restaurant_slug || '');

      if (!nextSlug) {
        this.$toast.error('Restaurant slug is required.');
        return;
      }

      if (nextSlug === currentSlug) {
        return;
      }

      this.$confirm({
        title: 'Confirm slug change',
        message: `Change slug from "${currentSlug}" to "${nextSlug}"? The old public menu URL will not redirect.`,
        button: { no: 'No', yes: 'Yes' },
        callback: (confirm) => {
          if (confirm) {
            this.updateSlug();
          }
        },
      });
    },

    async updateSlug() {
      const previousSlug = this.item.restaurant_slug || '';
      this.slugForm.saving = true;

      try {
        const { data } = await this.$axios.patch(
          this.$backend.RESTAURANT.UPDATE_SLUG.replace("{id}", this.item.id),
          { restaurant_slug: this.slugForm.restaurant_slug }
        );

        this.item.restaurant_slug = data.restaurant_slug;
        this.item.restaurant_slug_path = data.restaurant_slug_path;
        this.slugForm.restaurant_slug = data.restaurant_slug;

        this.$toast.success(data.changed
          ? `Slug updated from ${data.old_restaurant_slug} to ${data.restaurant_slug}.`
          : 'Slug already up to date.'
        );
      } catch (error) {
        this.slugForm.restaurant_slug = previousSlug;
        const validationError = error.response?.data?.errors?.restaurant_slug;
        this.$toast.error(
          Array.isArray(validationError)
            ? validationError.join(', ')
            : error.response?.data?.message || 'Failed to update slug.'
        );
      } finally {
        this.slugForm.saving = false;
      }
    },

    selectHeroImage() {
      this.$store.commit('set', ['changeHeroImageAsideShow', true]);
    },
    async changedHeroImage(image) {
      try {
        const { data } = await this.$axios.post(this.$backend.RESTAURANT.ASSIGN_HERO_IMAGE, {
          "image_id": image.id,
          "restaurants": this.item.id.toString(),
        });
        this.item.hero_image = image;
        this.$toast.success(`${data.message}`);

        // Show warnings to operator but continue
        if (data?.warnings) {
          const dups = data.warnings.duplicateHeroInZip?.length || 0;
          if (dups > 0) {
            const restaurants = data.warnings.duplicateHeroInZip.map(zip => zip.restaurants.map(restaurant => restaurant.name).join(', ')).join(', ');
            this.$toast.warning(
              `Warning: The hero image is used by multiple restaurants in the same zip: ${restaurants}`, { timeout: 0 });
          }
        }
      } catch (error) {
        this.$toast.error(typeof this.errors === 'object'
          ? Object.values(this.errors).join()
          : this.errors
        );
      }
    },

    changelogo() {
      this.$store.commit('set', ['changeLogoAsideShow', true]);
    },
    changedlogo(logo_path) {
      this.item.restaurant_logo_path = logo_path;
    },

    showLatestActivities() {
      this.$store.commit('set', ['latestRestaurantActivitiesAsideShow', true]);
    },
    showMessages() {
      this.$store.commit('restaurants/setSelectedRestaurant', this.item.id)
      this.$router.push({ name: "Message List" });
    },
    sendRestaurantDetails(detail) {
      const parseEmails = (emailsString) =>
        emailsString
          .split(';')
          .map(email => email.trim())
          .filter(email => email); // Filter out empty strings

      const allEmails = [
        parseEmails(this.item.email || ''),
        parseEmails(this.item.eigenaar1_email || ''),
        parseEmails(this.item.eigenaar2_email || ''),
      ].flat();

      this.uniqueEmails = Array.from(new Set(allEmails));
      this.detailToSend = detail;

      this.$store.commit('set', ['sendRestaurantDetailsAsideShow', true]);
    },
  }
};
</script>

<style scoped>
.representative-badge {
  align-items: center;
  display: inline-flex;
  gap: 0.25rem;
  padding-right: 0.25rem;
}

.representative-remove {
  color: inherit;
  line-height: 1;
  min-width: 1rem;
}

.widget:not(.device-widget):not(.fcm-device-widget) .card-footer {
  height: 44px;
  padding-left: 1rem !important;
}

.delivery-distance-widget .card-footer {
  height: auto !important;
  min-height: 44px;
  padding-top: 0.35rem !important;
  padding-bottom: 0.35rem !important;
}

.delivery-distance-widget__meta {
  color: #768192;
  font-size: 0.8rem !important;
  font-weight: 600;
  line-height: 1.2;
}

.delivery-distance-widget__orders {
  color: #9aa5b1;
  font-weight: 400;
}

.device-widget .card-footer,
.fcm-device-widget .card-footer {
  padding: 0.5rem 0.25rem !important;
}

.address-container {
  position: relative;
  background-color: #fff;
  opacity: 0.75;
  border-radius: 0.25rem;
}

.button {
  opacity: 0;
  transition: opacity .35s ease;
  background: rgba(0, 0, 0, .3);
  text-align: center;
  position: absolute;
  width: 100%;
  border-radius: 25px;
}

.button a {
  text-align: center;
  color: white;
  z-index: 1;
}

.address-container:hover .button {
  opacity: 1;
}

.resto-active {
  border: 0.375rem solid #64A64A;
}

.resto-none-active {
  border: 0.375rem solid #B32638;
}

/* Change logo */
.logo {
  position: relative;
  border-radius: 50%;
}

.logo:hover .logo__content {
  opacity: 1;
}
.logo:hover {
  overflow: hidden;
  background-color: #111;
}
.logo:hover .logo__image {
  opacity: .5;
}
.logo__image {
  border-radius: 50% !important;
  background-color: #fff !important;
  width: 150px;
  height: 150px;
  object-fit: cover;
  opacity: 1;
  transition: opacity .2s ease-in-out;
}
.logo__content {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  opacity: 0;
  transition: opacity .2s ease-in-out;
}
.logo__icon {
  color: white;
  padding-bottom: 8px;
}
.logo__text {
  text-transform: uppercase;
  font-size: 12px;
  width: 50%;
  text-align: center;
}
.internal-note-panel {
  background: #f8f9fa;
  border: 1px solid #d8dbe0;
  border-left: 4px solid #f9b115;
  border-radius: 0.25rem;
  padding: 1rem;
}

.restaurant-history-panel {
  background: #fff;
  border: 1px solid #d8dbe0;
  border-left: 4px solid #3399ff;
  border-radius: 0.25rem;
  padding: 1rem;
}

.opening-times-summary {
  background: #f8f9fa;
  border: 1px solid #d8dbe0;
  border-radius: 0.25rem;
  padding: 0.75rem;
}

.opening-times-summary__item {
  align-items: center;
  background: #fff;
  border: 1px solid #e4e7ea;
  border-radius: 0.25rem;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
}

.opening-times-summary__item code,
.today-custom-times code {
  color: #9b0a0a;
  font-size: 0.95rem;
  font-weight: 600;
}

.today-custom-times__row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
</style>

<style>
.device-widget .tab-item>a {
  height: 32px !important;
  padding: 5px !important;
}

.device-widget .text-muted,
.fcm-device-widget .text-muted {
  font-size: 0.8rem !important;
}

.c-chart-brand {
  position: absolute;
  width: 100%;
  height: 100px;
}

.widget .card,
.widget .card-footer {
  background-color: #f8f8ff;
}

.widget .text-value {
  font-size: 1rem;
}

.widget .text-muted {
  font-size: 1.5rem;
}

.delivery-distance-widget .text-value {
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.2;
  text-transform: none;
  white-space: nowrap;
}

.delivery-distance-widget .text-muted {
  font-size: 0.8rem;
}

.resto-header {
  position: relative;
  display: inline-block;
  background-repeat: no-repeat;
  background-position: center center;
  -webkit-background-size: cover;
  -moz-background-size: cover;
  -o-background-size: cover;
  background-size: cover;
}

.resto-header .action {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #3498db;
  color: #fff;
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.4s ease-in-out;
  backface-visibility: hidden;
  opacity: 0;
}

.resto-header:hover .action {
  opacity: 1;
}
</style>
